import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle, XCircle, CheckCircle2, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { runPublishCheck, drainEmbeddingQueue, autoApplyInternalLinks, embedArticle } from "@/lib/contentOps/api";
import type { ArticleRow, PublishCheckResult } from "@/lib/contentOps/types";
import PublishObservability from "../PublishObservability";

interface Props {
  article: ArticleRow;
  onSavePatch: (p: Partial<ArticleRow>) => Promise<void>;
  onReload: () => Promise<void>;
}

const PublishTab = ({ article, onSavePatch, onReload }: Props) => {
  const [check, setCheck] = useState<PublishCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState("");
  const [autoLinking, setAutoLinking] = useState(false);

  const autoLink = async () => {
    setAutoLinking(true);
    try {
      // Make sure the article has an embedding so semantic match works
      try { await embedArticle(article.id); } catch { /* ignore, may already exist */ }
      const r = await autoApplyInternalLinks(article.id);
      if (r.applied > 0) {
        toast.success(`Added ${r.applied} internal link${r.applied === 1 ? "" : "s"}`);
        await onReload();
        await run();
      } else {
        toast.info("No matching anchors found", {
          description: r.reason === "no_anchors_found"
            ? "No related article titles or keywords appear in the body."
            : r.reason || "Nothing to link.",
        });
      }
    } catch (e) {
      toast.error("Auto-link failed", { description: (e as Error).message });
    } finally { setAutoLinking(false); }
  };


  const run = async () => {
    setLoading(true);
    try {
      const r = await runPublishCheck(article.id);
      setCheck(r);
    } catch (e) {
      toast.error("Check failed", { description: (e as Error).message });
    } finally { setLoading(false); }
  };

  useEffect(() => { run(); }, [article.id]);

  const publish = async () => {
    if (!check?.ready) { toast.error("Resolve blocking issues first"); return; }
    await onSavePatch({ status: "published", published: true });
    toast.success("Published");
    // Embed-on-publish: trigger enqueues the job; drain the worker immediately
    // so semantic search / internal-link suggestions reflect the new article
    // without waiting for the next cron tick.
    try {
      const res = await drainEmbeddingQueue();
      if (res.done > 0) toast.success(`Embedding refreshed (${res.done})`);
      else if (res.errors > 0) toast.warning("Embedding queued, will retry");
    } catch (e) {
      toast.warning("Embedding queued, worker will retry", { description: (e as Error).message });
    }
    await onReload();
  };

  const schedule = async () => {
    if (!scheduling) return;
    await onSavePatch({ status: "ready", scheduled_for: new Date(scheduling).toISOString() });
    toast.success("Scheduled");
    await onReload();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
            <ShieldCheck size={14} /> Publish readiness
          </h3>
          <Button size="sm" variant="ghost" onClick={run} disabled={loading}
            className="h-7 px-2 text-xs" style={{ color: "hsl(0 0% 70%)" }}>
            {loading ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
            Re-run
          </Button>
        </div>

        {!check ? (
          <div className="flex items-center justify-center h-32" style={{ color: "hsl(0 0% 50%)" }}>
            <Loader2 size={16} className="animate-spin mr-2" /> Checking…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4 rounded-lg border p-4"
              style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 16%)" }}>
              <div className="text-4xl font-bold tabular-nums"
                style={{ color: check.score >= 80 ? "hsl(142 70% 55%)" : check.score >= 60 ? "hsl(38 92% 60%)" : "hsl(0 75% 60%)" }}>
                {check.score}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: "hsl(0 0% 90%)" }}>
                  {check.ready ? "Ready to publish" : `${check.fails} blocking issue${check.fails === 1 ? "" : "s"}`}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "hsl(0 0% 55%)" }}>
                  {check.passes} pass · {check.warns} warn · {check.fails} fail
                </div>
              </div>
            </div>

            <ul className="space-y-1.5">
              {check.checks.map((c) => {
                const Icon = c.status === "pass" ? CheckCircle2 : c.status === "warn" ? AlertTriangle : XCircle;
                const color = c.status === "pass" ? "hsl(142 70% 55%)" : c.status === "warn" ? "hsl(38 92% 60%)" : "hsl(0 75% 60%)";
                const isInternal = c.id === "internal";
                return (
                  <li key={c.id} className="flex items-start gap-3 rounded-lg border p-2.5"
                    style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 14%)" }}>
                    <Icon size={14} style={{ color }} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm" style={{ color: "hsl(0 0% 88%)" }}>{c.label}</div>
                      {c.message && <div className="text-[11px]" style={{ color: "hsl(0 0% 55%)" }}>{c.message}</div>}
                    </div>
                    {c.status !== "pass" && isInternal && (
                      <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                        <Button size="sm" onClick={autoLink} disabled={autoLinking}
                          className="h-7 px-2.5 text-[11px] font-semibold text-white"
                          style={{ background: "hsl(207 68% 50%)" }}>
                          {autoLinking ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
                          Auto-link related
                        </Button>
                      </div>
                    )}
                    {c.status !== "pass" && !isInternal && c.fix && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        // Mark that we are away fixing something; ArticleWorkspace
                        // will re-check on save and auto-return to Publish when ready.
                        window.dispatchEvent(new CustomEvent("contentops:awaiting-fix", { detail: { articleId: article.id } }));
                        window.dispatchEvent(new CustomEvent("contentops:jump", { detail: c.fix }));
                      }}
                        className="h-7 px-2 text-[11px] flex-shrink-0"
                        style={{ color: "hsl(207 68% 65%)" }}>
                        Fix →
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>

          </>
        )}
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: "hsl(0 0% 90%)" }}>Publish now</h3>
          <p className="text-xs mb-3" style={{ color: "hsl(0 0% 55%)" }}>
            Status moves to <strong>Published</strong> and the article becomes visible on /blog.
          </p>
          <Button onClick={publish} disabled={!check?.ready || article.status === "published"} className="w-full"
            style={{ background: check?.ready ? "hsl(142 70% 40%)" : "hsl(0 0% 14%)", color: "white" }}>
            <Send size={14} className="mr-1" />
            {article.status === "published" ? "Already published" : "Publish"}
          </Button>
        </div>

        <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
            <Clock size={14} /> Schedule
          </h3>
          <p className="text-xs mb-3" style={{ color: "hsl(0 0% 55%)" }}>
            Sets status to <strong>Ready</strong> with a scheduled publish time. Auto-publishing runs on a nightly cron (P2).
          </p>
          <Input type="datetime-local" value={scheduling} onChange={(e) => setScheduling(e.target.value)}
            className="border mb-2" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
          <Button onClick={schedule} disabled={!scheduling} variant="outline" className="w-full border"
            style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)", background: "transparent" }}>
            Schedule
          </Button>
          {article.scheduled_for && (
            <p className="text-[11px] mt-2" style={{ color: "hsl(38 92% 65%)" }}>
              Scheduled for {new Date(article.scheduled_for).toLocaleString()}
            </p>
          )}
        </div>

        <PublishObservability articleId={article.id} />
      </div>
    </div>
  );
};

export default PublishTab;
